import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader, Sparkles, RotateCcw } from 'lucide-react';
import API from '../api/client';
import ReactMarkdown from 'react-markdown';

const MODEL_OPTIONS = [
  { value: 'gpt', label: 'GPT-5.2', badge: 'OpenAI', color: 'emerald' },
  { value: 'gemini', label: 'Gemini Flash', badge: 'Google', color: 'blue' },
];

export default function FloatingAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState('gpt');
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const aiMsg = { role: 'assistant', content: '', id: Date.now() + 1, streaming: true };
    setMessages(prev => [...prev, aiMsg]);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId, model }),
      });

      if (!resp.ok) throw new Error('Erreur serveur');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      let buffer = '';
      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // On ne traite que les lignes complètes (une trame SSE peut être coupée entre deux chunks)
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let data;
          try { data = JSON.parse(line.slice(6)); } catch { continue; }
          if (data.error) throw new Error(data.error);      // remonte à l'affichage (bulle rouge)
          if (data.done) { streamDone = true; break; }
          if (data.text) {
            accumulated += data.text;
            setMessages(prev => prev.map(m =>
              m.id === aiMsg.id ? { ...m, content: accumulated } : m
            ));
          }
        }
      }
      if (!accumulated) throw new Error("Aucune réponse de l'IA. Vérifiez la configuration OpenAI (clé et crédits).");
      setMessages(prev => prev.map(m =>
        m.id === aiMsg.id ? { ...m, streaming: false } : m
      ));
    } catch (e) {
      setMessages(prev => prev.map(m =>
        m.id === aiMsg.id ? { ...m, content: `Erreur: ${e.message}`, streaming: false, error: true } : m
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => setMessages([]);

  const SelectedModel = MODEL_OPTIONS.find(m => m.value === model);

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="ai-chat-toggle-btn"
        onClick={() => setOpen(v => !v)}
        className={`no-print fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${open ? 'bg-stone-800 rotate-0' : 'bg-emerald-600 hover:bg-emerald-700'}`}
      >
        {open ? <X size={22} className="text-white" /> : <Sparkles size={22} className="text-white" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="no-print fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
          style={{ height: '520px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-600 text-white">
            <div className="flex items-center gap-2">
              <Sparkles size={16} />
              <span className="font-semibold text-sm">MedAssist IA</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Model selector */}
              <select
                data-testid="ai-model-selector"
                value={model}
                onChange={e => setModel(e.target.value)}
                className="text-xs bg-emerald-700 text-white border-0 rounded px-2 py-1 cursor-pointer focus:outline-none"
              >
                {MODEL_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <button onClick={clearChat} title="Effacer" className="p-1 hover:bg-emerald-700 rounded transition-colors">
                <RotateCcw size={14} />
              </button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-emerald-700 rounded transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles size={22} className="text-emerald-600" />
                </div>
                <p className="text-stone-600 text-sm font-medium">Bonjour Dr. !</p>
                <p className="text-stone-400 text-xs mt-1">Posez-moi vos questions médicales.</p>
                <div className="mt-4 space-y-2">
                  {['Diagnostics différentiels pour fièvre + toux', 'Posologie Amoxicilline adulte', 'Critères HTA grade 2'].map(q => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="block w-full text-left text-xs bg-white border border-stone-200 rounded-lg px-3 py-2 text-stone-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-tr-sm'
                    : msg.error
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-tl-sm'
                      : 'bg-white text-stone-800 border border-stone-200 rounded-tl-sm shadow-sm'
                }`}>
                  {msg.role === 'assistant' && !msg.error ? (
                    <div className="prose prose-sm prose-stone max-w-none">
                      <ReactMarkdown>{msg.content || ' '}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                  {msg.streaming && (
                    <span className="inline-flex gap-0.5 ml-1">
                      <span className="w-1 h-1 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-stone-200 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="ai-chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question médicale..."
                rows={2}
                className="flex-1 resize-none px-3 py-2 border border-stone-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              />
              <button
                data-testid="ai-chat-send-btn"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="p-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl transition-colors flex-shrink-0"
              >
                {loading ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5 text-center">
              {SelectedModel?.label} · Shift+Entrée pour nouvelle ligne
            </p>
          </div>
        </div>
      )}
    </>
  );
}
